import React from "react";
import StandalonePageLayout from "../layout/StandalonePageLayout";
import { PageOpening, Section, SectionTitle, MarginNote, Prose, Ledger } from "../../public/Journal";
import Reveal from "../../public/Reveal";

function Accessibility() {
    const features = [
        {
            title: "Keyboard Navigation",
            description: "Full keyboard support for all interactive elements. Navigate using Tab, Enter, and Arrow keys.",
            details: [
                "Tab through all interactive elements",
                "Enter to activate buttons and links",
                "Arrow keys for dropdown menus and sliders",
                "Escape to close modals and dialogs"
            ]
        },
        {
            title: "Screen Reader Support",
            description: "Compatible with popular screen readers including JAWS, NVDA, and VoiceOver.",
            details: [
                "Semantic HTML structure",
                "ARIA labels and landmarks",
                "Alternative text for images",
                "Descriptive link text"
            ]
        },
        {
            title: "Color Contrast",
            description: "High contrast ratios meeting WCAG 2.1 Level AA standards for text and interactive elements.",
            details: [
                "Minimum 4.5:1 contrast ratio for text",
                "7:1 ratio for large text",
                "Color is not the only visual means of conveying information",
                "Focus indicators with sufficient contrast"
            ]
        },
        {
            title: "Resizable Text",
            description: "Text can be resized up to 200% without loss of content or functionality.",
            details: [
                "Use browser zoom controls",
                "Responsive text sizing",
                "No horizontal scrolling at 200% zoom",
                "Readable at different screen sizes"
            ]
        },
        {
            title: "Pointer Gestures",
            description: "All functionality available through simple pointer actions.",
            details: [
                "Large clickable areas (minimum 44x44 pixels)",
                "No path-based or multipoint gestures required",
                "Alternative input methods supported",
                "Hover and focus states clearly visible"
            ]
        },
        {
            title: "Responsive Design",
            description: "Adapts to different screen sizes and orientations without loss of information.",
            details: [
                "Mobile-first responsive layout",
                "Portrait and landscape orientation support",
                "Consistent experience across devices",
                "Touch-friendly interface elements"
            ]
        }
    ];

    const shortcuts = [
        { keys: "Tab", action: "Navigate to next interactive element" },
        { keys: "Shift + Tab", action: "Navigate to previous interactive element" },
        { keys: "Enter or Space", action: "Activate buttons and links" },
        { keys: "Escape", action: "Close modals and dialogs" },
        { keys: "Arrow Keys", action: "Navigate within menus and lists" },
        { keys: "Ctrl/Cmd + F", action: "Open search functionality" }
    ];

    const complianceStandards = [
        {
            standard: "WCAG 2.1 Level AA",
            description: "We strive to meet Web Content Accessibility Guidelines 2.1 at Level AA conformance.",
            status: "Compliant"
        },
        {
            standard: "Section 508",
            description: "Compatible with U.S. Section 508 accessibility standards for federal agencies.",
            status: "Compliant"
        },
        {
            standard: "ADA",
            description: "Designed to be accessible under the Americans with Disabilities Act guidelines.",
            status: "Compliant"
        }
    ];

    const ruledList = (items, topRule = true) => (
        <ul className={topRule ? "border-t border-ink/15 dark:border-paper/15" : ""}>
            {items.map((item, idx) => (
                <li key={idx} className="grid grid-cols-[40px_1fr] border-b border-ink/15 py-2.5 font-plex text-[15px] text-ink/75 dark:border-paper/15 dark:text-paper/75">
                    <span className="font-plex-mono text-[11px] leading-[22px] text-ink/40 dark:text-paper/40">{String(idx + 1).padStart(2, "0")}</span>
                    <span>{item}</span>
                </li>
            ))}
        </ul>
    );

    return (
        <StandalonePageLayout>
            <PageOpening
                section="§ Statement"
                label="Accessibility"
                title="Accessibility statement."
                intro="JPRO is committed to ensuring digital accessibility for people of all abilities. We continuously work to improve the user experience for everyone and apply relevant accessibility standards."
            />

            {/* Accessibility Features */}
            <Section
                number="01"
                label="Features"
                aside={<MarginNote>Six provisions, each with the specific behaviours we support.</MarginNote>}
            >
                <SectionTitle>Accessibility features</SectionTitle>
                <div className="mt-10">
                    {features.map((feature, index) => (
                        <Reveal
                            key={index}
                            className="grid gap-4 border-t border-ink/15 py-8 md:grid-cols-[72px_1fr_1fr] md:gap-8 dark:border-paper/15"
                        >
                            <span className="font-plex-mono text-[12px] text-stamp">1.{index + 1}</span>
                            <div>
                                <h3 className="font-news text-[26px] leading-tight">{feature.title}</h3>
                                <p className="mt-3 font-plex text-[15.5px] leading-relaxed text-ink/70 dark:text-paper/70">{feature.description}</p>
                            </div>
                            {ruledList(feature.details, false)}
                        </Reveal>
                    ))}
                </div>
            </Section>

            {/* Keyboard Shortcuts */}
            <Section number="02" label="Keyboard">
                <SectionTitle>Keyboard shortcuts</SectionTitle>
                <table className="mt-10 w-full border-collapse text-left">
                    <thead>
                        <tr className="border-b border-ink/70 font-plex-mono text-[11px] uppercase tracking-[0.18em] text-ink/50 dark:border-paper/60 dark:text-paper/50">
                            <th className="py-3 pr-6 font-normal">Shortcut</th>
                            <th className="py-3 font-normal">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {shortcuts.map((shortcut, index) => (
                            <tr key={index} className="border-b border-ink/15 dark:border-paper/15">
                                <td className="py-4 pr-6 align-top">
                                    <kbd className="whitespace-nowrap border border-ink/30 px-2 py-0.5 font-plex-mono text-[12.5px] dark:border-paper/30">
                                        {shortcut.keys}
                                    </kbd>
                                </td>
                                <td className="py-4 font-plex text-[15.5px] text-ink/80 dark:text-paper/80">{shortcut.action}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Section>

            {/* Compliance Standards */}
            <Section
                number="03"
                label="Standards"
                aside={<MarginNote>Status reflects our own assessment against each standard.</MarginNote>}
            >
                <SectionTitle>Compliance standards</SectionTitle>
                <div className="mt-10">
                    {complianceStandards.map((standard, index) => (
                        <Reveal
                            key={index}
                            className="grid gap-3 border-t border-ink/15 py-6 sm:grid-cols-[1fr_auto] sm:gap-8 dark:border-paper/15"
                        >
                            <div>
                                <h3 className="font-news text-[24px] leading-tight">{standard.standard}</h3>
                                <p className="mt-2 max-w-xl font-plex text-[15.5px] leading-relaxed text-ink/70 dark:text-paper/70">{standard.description}</p>
                            </div>
                            <span className="self-start font-plex-mono text-[12px] uppercase tracking-[0.16em] text-ink dark:text-paper">
                                {standard.status}
                            </span>
                        </Reveal>
                    ))}
                </div>
            </Section>

            {/* Browser and AT Compatibility */}
            <Section number="04" label="Compatibility">
                <SectionTitle>Compatibility</SectionTitle>
                <div className="mt-10 grid gap-10 md:grid-cols-2">
                    <div>
                        <h3 className="mb-4 font-plex-mono text-[11.5px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55">Supported browsers</h3>
                        {ruledList(["Chrome (latest)", "Firefox (latest)", "Safari (latest)", "Edge (latest)"])}
                    </div>
                    <div>
                        <h3 className="mb-4 font-plex-mono text-[11.5px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55">Assistive technologies</h3>
                        {ruledList(["JAWS", "NVDA", "VoiceOver", "TalkBack"])}
                    </div>
                </div>
            </Section>

            {/* Feedback Section */}
            <Section
                number="05"
                label="Feedback"
                aside={<MarginNote>We aim to respond within 2 business days.</MarginNote>}
            >
                <SectionTitle>Feedback &amp; support</SectionTitle>
                <Prose className="mt-6">
                    <p>
                        We welcome your feedback on the accessibility of JPRO. Please let us know if you
                        encounter accessibility barriers:
                    </p>
                </Prose>
                <Ledger
                    className="mt-8 max-w-3xl"
                    rows={[
                        ["Email", <a key="e" href="mailto:jpro@zeffert.com" className="link-underline">jpro@zeffert.com</a>],
                        ["Subject line", "\u201cAccessibility Feedback\u201d"],
                        ["Response time", "We aim to respond within 2 business days"],
                    ]}
                />
                <Prose className="mt-8">
                    <p className="text-[15px]">
                        We take your feedback seriously and will consider it as we evaluate ways to
                        accommodate all of our customers and our overall accessibility policies.
                    </p>
                </Prose>
            </Section>

            {/* Technical Specifications */}
            <Section number="06" label="Technical">
                <SectionTitle>Technical specifications</SectionTitle>
                <Prose className="mt-6">
                    <p>Accessibility of JPRO relies on the following technologies to work:</p>
                </Prose>
                <div className="mt-6 max-w-md">{ruledList(["HTML5", "CSS3", "JavaScript (ES6+)", "WAI-ARIA"])}</div>
                <Prose className="mt-8">
                    <p className="text-[15px]">
                        These technologies are relied upon for conformance with the accessibility
                        standards used. Disabling JavaScript may impact accessibility features.
                    </p>
                </Prose>
            </Section>
        </StandalonePageLayout>
    );
}

export default Accessibility;
